/**
 * Column charts: BarChart (grouped), StackedBarChart (composition) and Histogram (distribution, ordinal blue ramp).
 * Bars <= 24 px, 4 px rounded data end, square at the baseline, 2 px surface gap. Each bar's hit area is its whole
 * column slice (bigger than the mark), focusable, Enter drills. A null value draws no bar (never a zero bar) and its
 * tooltip says "no data". Owner: slice web-charts (#1115).
 */
import * as React from 'react'
import { XLabels, YGrid } from './axes'
import { HEIGHT, MARGIN, MARK, seqColor } from './chart-theme'
import { ChartTooltip, type TooltipRow, tooltipText } from './chart-tooltip'
import { axisFormatter, xLabel } from './format'
import { labelEvery, linear, niceTicks, useWidth, vBarPath } from './geometry'
import { summarise } from './line-chart'
import { MarkButton } from './mark-button'
import { buildModel, type ChartProps, type PlotModel } from './model'

type Mode = 'grouped' | 'stacked' | 'histogram'

export function BarChart(props: ChartProps) {
  return <ColumnChart {...props} mode="grouped" />
}

export function StackedBarChart(props: ChartProps) {
  return <ColumnChart {...props} mode="stacked" />
}

export function Histogram(props: ChartProps) {
  return <ColumnChart {...props} mode="histogram" />
}

interface Mark {
  i: number
  s: number
  x: number
  w: number
  base: number
  end: number
  value: number | null
  color: string
  rounded: boolean
}

function stackExtent(model: PlotModel): [number, number] {
  let lo = 0
  let hi = 0
  model.xs.forEach((_, i) => {
    let pos = 0
    let neg = 0
    for (const p of model.plotted) {
      const v = p.values[i]
      if (v === null || v === undefined) continue
      if (v >= 0) pos += v
      else neg += v
    }
    hi = Math.max(hi, pos)
    lo = Math.min(lo, neg)
  })
  return [lo, hi]
}

function ColumnChart({ chart, onPointClick, canSee, mode: requested }: ChartProps & { mode: Mode }) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const model = React.useMemo(() => buildModel(chart, canSee), [chart, canSee])
  const [hover, setHover] = React.useState<{ i: number; s: number } | null>(null)
  const { xs, plotted } = model
  // A histogram is one distribution; with several series it reads as grouped bars in their categorical colours.
  const mode: Mode = requested === 'histogram' && plotted.length > 1 ? 'grouped' : requested
  const height = HEIGHT.bar
  const left = MARGIN.left
  const right = width - MARGIN.right
  const top = MARGIN.top
  const bottom = height - MARGIN.bottom
  const [lo, hi] = mode === 'stacked' ? stackExtent(model) : [model.min, model.max]
  const ticks = niceTicks(lo, hi)
  const y = linear(ticks[0] ?? 0, ticks[ticks.length - 1] ?? 1, bottom, top)
  const zero = y(0)
  const pw = Math.max(1, right - left)
  const band = xs.length > 0 ? pw / xs.length : pw
  const xAt = (i: number) => left + band * i + band / 2
  const fmt = axisFormatter(chart.unit, Math.max(Math.abs(lo), Math.abs(hi)))
  const labels = xs.map((x) => xLabel(chart, x))
  const every = labelEvery(xs.length, pw, 56)

  const marks: Mark[] = []
  if (mode === 'stacked') {
    const w = Math.max(2, Math.min(MARK.barMax, band - MARK.gap * 2))
    xs.forEach((_, i) => {
      let pos = 0
      let neg = 0
      const at: Mark[] = []
      plotted.forEach((p, s) => {
        const v = p.values[i] ?? null
        if (v === null) {
          at.push({ i, s, x: xAt(i) - w / 2, w, base: zero, end: zero, value: null, color: p.color, rounded: false })
          return
        }
        const from = v >= 0 ? pos : neg
        const to = from + v
        if (v >= 0) pos = to
        else neg = to
        at.push({ i, s, x: xAt(i) - w / 2, w, base: y(from), end: y(to), value: v, color: p.color, rounded: false })
      })
      // Round only the outermost segment on each side; a 2 px surface gap separates touching segments.
      const lastPos = [...at].reverse().find((m) => m.value !== null && m.value > 0)
      const lastNeg = [...at].reverse().find((m) => m.value !== null && m.value < 0)
      for (const m of at) {
        if (m === lastPos || m === lastNeg) m.rounded = true
        else if (m.value !== null && m.value !== 0)
          m.end = m.value > 0 ? Math.min(m.base, m.end + MARK.gap) : Math.max(m.base, m.end - MARK.gap)
        marks.push(m)
      }
    })
  } else if (mode === 'histogram') {
    const p = plotted[0]
    const w = Math.max(2, band - MARK.gap)
    if (p) {
      xs.forEach((_, i) => {
        const v = p.values[i] ?? null
        const t = xs.length <= 1 ? 1 : i / (xs.length - 1)
        marks.push({
          i,
          s: 0,
          x: left + band * i + MARK.gap / 2,
          w,
          base: zero,
          end: v === null ? zero : y(v),
          value: v,
          color: seqColor(t, 1),
          rounded: true,
        })
      })
    }
  } else {
    const k = Math.max(1, plotted.length)
    const groupMax = band - Math.max(MARK.gap * 2, band * 0.2)
    const w = Math.max(2, Math.min(MARK.barMax, (groupMax - MARK.gap * (k - 1)) / k))
    const groupW = w * k + MARK.gap * (k - 1)
    xs.forEach((_, i) => {
      plotted.forEach((p, s) => {
        const v = p.values[i] ?? null
        const x = xAt(i) - groupW / 2 + s * (w + MARK.gap)
        marks.push({ i, s, x, w, base: zero, end: v === null ? zero : y(v), value: v, color: p.color, rounded: true })
      })
    })
  }

  const rowsAt = (i: number, s: number): TooltipRow[] => {
    if (mode === 'stacked') {
      return plotted.map((p) => ({ key: p.key, label: p.label, color: p.color, value: p.values[i] ?? null, n: p.ns[i] ?? null }))
    }
    const p = plotted[s]
    if (!p) return []
    return [
      { key: p.key, label: p.label, color: mode === 'histogram' ? undefined : p.color, value: p.values[i] ?? null, n: p.ns[i] ?? null },
    ]
  }

  const drill = (i: number, s: number) => {
    const p = plotted[s]
    const x = xs[i]
    if (!onPointClick || !p || x === undefined || (p.values[i] ?? null) === null) return
    onPointClick({ x, series: p.key })
  }

  const hovered = hover ? marks.find((m) => m.i === hover.i && m.s === hover.s) : undefined
  const kindWord = mode === 'stacked' ? 'Stacked bar chart' : mode === 'histogram' ? 'Histogram' : 'Bar chart'

  return (
    <div
      ref={ref}
      className="reports-root relative w-full"
      data-chart-kind={requested === 'grouped' ? 'bar' : requested === 'stacked' ? 'stacked_bar' : 'histogram'}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={summarise(kindWord, chart.title, plotted, labels[0] ?? '', labels[labels.length - 1] ?? '', xs.length)}
        className="block overflow-visible"
      >
        <YGrid ticks={ticks} y={y} left={left} right={right} format={fmt} />
        <XLabels labels={labels} xAt={xAt} y={height - 8} every={every} />
        {marks.map((m) => {
          const p = plotted[m.s]
          if (!p || m.value === null || Math.abs(m.end - m.base) <= 0) return null
          const lifted = hover?.i === m.i && hover.s === m.s
          return (
            <path
              key={`${m.i}:${m.s}`}
              data-mark-paint
              data-x={xs[m.i]}
              data-series={p.key}
              d={vBarPath(m.x, m.w, m.base, m.end, m.rounded ? MARK.radius : 0)}
              fill={m.color}
              style={lifted ? { filter: 'brightness(1.12)' } : undefined}
            />
          )
        })}
      </svg>
      <div className="absolute top-0 left-0 w-full" style={{ height }}>
        {marks.map((m) => {
          const p = plotted[m.s]
          if (!p) return null
          const live = m.value !== null
          // Stacked: each segment is its own target (the column is shared); a missing segment has none.
          if (mode === 'stacked' && !live) return null
          const title = xLabel(chart, xs[m.i] ?? '', true)
          const label = tooltipText(
            title,
            rowsAt(m.i, m.s).filter((r) => r.key === p.key),
            chart.unit,
          )
          // Hit slice: the column (or the series' share of it), full plot height; a stacked segment plus its gap.
          const hitX = mode === 'grouped' ? m.x - MARK.gap / 2 : left + band * m.i
          const hitW = mode === 'grouped' ? m.w + MARK.gap : band
          const hitY = mode === 'stacked' ? Math.min(m.base, m.end) - MARK.gap / 2 : top
          const hitH = mode === 'stacked' ? Math.max(6, Math.abs(m.end - m.base) + MARK.gap) : Math.max(0, bottom - top)
          return (
            <MarkButton
              key={`${m.i}:${m.s}`}
              x={hitX}
              y={hitY}
              w={hitW}
              h={hitH}
              label={label}
              clickable={live && !!onPointClick}
              empty={!live}
              dataX={xs[m.i]}
              dataSeries={p.key}
              onHover={() => setHover({ i: m.i, s: m.s })}
              onLeave={() => setHover(null)}
              onActivate={() => drill(m.i, m.s)}
            />
          )
        })}
      </div>
      {hover && hovered ? (
        <ChartTooltip
          x={hovered.x + hovered.w / 2}
          y={Math.min(hovered.end, hovered.base) - 4}
          containerWidth={width}
          title={xLabel(chart, xs[hover.i] ?? '', true)}
          rows={rowsAt(hover.i, hover.s)}
          unit={chart.unit}
        />
      ) : null}
    </div>
  )
}
